import { type Ref, useEffect, useState } from 'react'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { Select, TextField as LsTextField } from '@loadsmart/loadsmart-ui'
import { Dialog, Icon, TextField, Layout, Field, Banner } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { transformValidationErrors } from 'core/utils/yup'

import { PHONE_WITH_COUNTRY_MASK } from 'legacy/constants/masks'

import { type NewEmployee } from '../../../domain/Employee'
import { useCreateAndAssignDriver } from '../../hooks/useCreateAndAssignDriver'

type DriverFormProps = {
  readonly carrierUUID: string
  readonly shipmentUUID: string
  readonly onCancel: () => void
  readonly onSave: () => void
}

type FormErrors = {
  name?: string
  phoneNumber?: string
  roles?: string
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  phoneNumber: Yup.string().required('Required'),
  roles: Yup.array().min(1, 'Required').required('Required'),
})

const employeeRoles = [
  {
    value: 'driver',
    label: 'Driver',
  },
  {
    value: 'dispatcher',
    label: 'Dispatcher',
  },
]

function getFieldStatus(error?: string) {
  return error ? 'danger' : 'default'
}

export function DriverForm({ carrierUUID, shipmentUUID, onCancel, onSave }: DriverFormProps) {
  const [driverToCreate, setDriverToCreate] = useState<Partial<NewEmployee>>({})
  const [errors, setErrors] = useState<FormErrors>({})
  const {
    createAndAssign,
    requestStatus,
    driver,
    error: createAndAssignError,
  } = useCreateAndAssignDriver()

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to create new driver')
    }

    if (createAndAssignError) {
      setErrors(createAndAssignError)
    }
  }, [requestStatus, createAndAssignError])

  useEffect(() => {
    if (requestStatus === RequestStatus.Done && driver) {
      onSave()
    }
  }, [driver, onSave, requestStatus])

  async function handleSave() {
    setErrors({})

    try {
      validationSchema.validateSync(driverToCreate, { abortEarly: false })

      await createAndAssign({
        carrierUUID,
        shipmentUUID,
        newEmployee: driverToCreate as NewEmployee,
      })
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setErrors(transformValidationErrors(error))
      }
    }
  }

  return (
    <>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          {requestStatus === RequestStatus.Error && (
            <Banner variant="danger">
              <Banner.Title>Failed to create the driver</Banner.Title>
              <Banner.Description>
                Please review the information below and try again.
              </Banner.Description>
              <Banner.Actions>
                <Banner.ActionPrimary onClick={handleSave}>Retry</Banner.ActionPrimary>
              </Banner.Actions>
            </Banner>
          )}

          <Field status={getFieldStatus(errors.name)} required>
            <Field.Label id="driver-name-label">Driver name</Field.Label>
            <TextField
              aria-labelledby="driver-name-label"
              placeholder="e.g. Jack Smith"
              onInput={event => {
                const name = (event.target as HTMLInputElement).value.trim()
                setDriverToCreate({ ...driverToCreate, name })
              }}
            />
            <Field.Hint>{errors.name}</Field.Hint>
          </Field>
          <Layout.Grid>
            <Field status={getFieldStatus(errors.phoneNumber)} required>
              <Field.Label id="driver-phone-label">Driver phone</Field.Label>
              <MaskedInput
                id="phoneNumber"
                name="phoneNumber"
                mask={PHONE_WITH_COUNTRY_MASK}
                placeholder="e. g. +1 123-456-789"
                aria-labelledby="driver-phone-label"
                aria-required="true"
                onChange={event => {
                  const phoneNumber = (event.target as HTMLInputElement).value
                  setDriverToCreate({ ...driverToCreate, phoneNumber })
                }}
                render={(ref, props) => (
                  <LsTextField ref={ref as Ref<HTMLInputElement>} {...props} type="tel" />
                )}
              />
              <Field.Hint>{errors.phoneNumber}</Field.Hint>
            </Field>
            <Field status={getFieldStatus(errors.roles)} required>
              <Field.Label>Role</Field.Label>
              <Select
                multiple
                placeholder="Select roles"
                id="roles"
                name="roles"
                options={employeeRoles}
                onChange={event => {
                  const roles =
                    event.target.value === null ? [] : (event.target.value as NewEmployee['roles'])
                  setDriverToCreate({ ...driverToCreate, roles })
                }}
              />
              <Field.Hint>{errors.roles}</Field.Hint>
            </Field>
          </Layout.Grid>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary
          onClick={onCancel}
          leading={<Icon name="back" size="16px" color="color-primary-100" />}
        >
          Back
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          onClick={handleSave}
          loading={requestStatus === RequestStatus.Pending}
        >
          Create and confirm
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}
